import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Box, Button, Chip, Grid, IconButton, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridDensity, GridToolbarProps } from '@mui/x-data-grid';
import { isEqual } from 'es-toolkit';
import { map } from 'es-toolkit/compat';
import { SessionDataset, UpdateSessionDatasetRequest } from 'interfaces/agentSession';
import React from 'react';

import DatasetToolbar from './StudyReportDatasetsToolbar';
import { buildPreviewTable, formatMatchedCaseField, getPrimaryJoinCandidate } from './studyReportDatasetsUtils';
import {
  buildStudyReportAutosizedColumns,
  buildStudyReportTableAutosizeOptions,
  buildStudyReportTableExportFileName,
  buildStudyReportTableRows,
} from './studyReportTableGrid';

interface SessionDatasetCardProps {
  dataset: SessionDataset;
  onDelete: (datasetId: string) => void;
  onSave: (datasetId: string, request: UpdateSessionDatasetRequest) => void;
  isSaving: boolean;
}

// Presentational/editor card for one uploaded dataset.
// Owns local metadata draft state and preview table rendering.
const SessionDatasetCard: React.FC<SessionDatasetCardProps> = ({ dataset, onDelete, onSave, isSaving }) => {
  // Build all DataGrid inputs from the dataset once and memoize for stable renders.
  const previewTable = React.useMemo(() => buildPreviewTable(dataset), [dataset]);
  const previewColumns = React.useMemo(() => buildStudyReportAutosizedColumns(previewTable), [previewTable]);
  const autosizeOptions = React.useMemo(() => buildStudyReportTableAutosizeOptions(previewTable), [previewTable]);
  const previewRows = React.useMemo(() => buildStudyReportTableRows(previewTable), [previewTable]);
  const csvFileName = React.useMemo(() => buildStudyReportTableExportFileName(previewTable), [previewTable]);
  const [density, setDensity] = React.useState<GridDensity>('compact');
  const toolbar = React.useMemo(
    () =>
      function Toolbar(props: GridToolbarProps) {
        return <DatasetToolbar {...props} csvFileName={csvFileName} />;
      },
    [csvFileName]
  );
  const primaryJoinCandidate = React.useMemo(() => getPrimaryJoinCandidate(dataset), [dataset]);
  const [draftMetadata, setDraftMetadata] = React.useState<UpdateSessionDatasetRequest>({
    displayName: dataset.metadata.displayName || '',
    description: dataset.metadata.description || '',
    kind: dataset.metadata.kind || '',
    preferredJoinColumn: dataset.metadata.preferredJoinColumn || '',
  });

  // Reset local form state whenever the backing dataset changes in cache/query.
  React.useEffect(() => {
    setDraftMetadata({
      displayName: dataset.metadata.displayName || '',
      description: dataset.metadata.description || '',
      kind: dataset.metadata.kind || '',
      preferredJoinColumn: dataset.metadata.preferredJoinColumn || '',
    });
  }, [dataset]);

  // Normalize empty strings to null so payload semantics match backend expectations.
  const normalizedDraft = React.useMemo<UpdateSessionDatasetRequest>(
    () => ({
      displayName: draftMetadata.displayName?.trim() || null,
      description: draftMetadata.description?.trim() || null,
      kind: draftMetadata.kind?.trim() || null,
      preferredJoinColumn: draftMetadata.preferredJoinColumn?.trim() || null,
    }),
    [draftMetadata]
  );
  const persistedMetadata = React.useMemo<UpdateSessionDatasetRequest>(
    () => ({
      displayName: dataset.metadata.displayName || null,
      description: dataset.metadata.description || null,
      kind: dataset.metadata.kind || null,
      preferredJoinColumn: dataset.metadata.preferredJoinColumn || null,
    }),
    [dataset]
  );
  // Save button is enabled only when normalized draft differs from persisted metadata.
  const hasUnsavedChanges = !isEqual(normalizedDraft, persistedMetadata);

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {dataset.metadata.displayName || dataset.fileName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dataset.fileName} • {dataset.rowCount} rows • {dataset.sourceType.toUpperCase()}
            </Typography>
          </Stack>
          <IconButton size="small" color="error" onClick={() => onDelete(dataset.id)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${dataset.columns.length} columns`} variant="outlined" />
          {primaryJoinCandidate ? (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={`${primaryJoinCandidate.datasetColumn} -> ${formatMatchedCaseField(
                primaryJoinCandidate.matchedCaseField
              )}`}
            />
          ) : (
            <Chip size="small" variant="outlined" label="No case join detected" />
          )}
          {primaryJoinCandidate && (
            <Chip
              size="small"
              variant="outlined"
              label={`${primaryJoinCandidate.matchedRowCount}/${dataset.rowCount} matched rows`}
            />
          )}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Display name"
              value={draftMetadata.displayName || ''}
              onChange={(event) =>
                setDraftMetadata((currentValue) => ({ ...currentValue, displayName: event.target.value }))
              }
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Dataset type"
              value={draftMetadata.kind || ''}
              onChange={(event) => setDraftMetadata((currentValue) => ({ ...currentValue, kind: event.target.value }))}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Preferred join column"
              value={draftMetadata.preferredJoinColumn || ''}
              onChange={(event) =>
                setDraftMetadata((currentValue) => ({
                  ...currentValue,
                  preferredJoinColumn: event.target.value,
                }))
              }
            >
              <MenuItem value="">Auto-detect</MenuItem>
              {map(dataset.columns, (column) => (
                <MenuItem key={column.name} value={column.name}>
                  {column.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={draftMetadata.description || ''}
              onChange={(event) =>
                setDraftMetadata((currentValue) => ({ ...currentValue, description: event.target.value }))
              }
            />
          </Grid>
        </Grid>

        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            {primaryJoinCandidate
              ? `Primary join uses ${primaryJoinCandidate.datasetColumn} against ${formatMatchedCaseField(
                  primaryJoinCandidate.matchedCaseField
                )}.`
              : 'The agent will still be able to inspect the dataset, but no automatic study-case join was detected yet.'}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            disabled={!hasUnsavedChanges || isSaving}
            onClick={() => onSave(dataset.id, normalizedDraft)}
          >
            Save metadata
          </Button>
        </Stack>

        <Box sx={{ width: '100%', height: 500 }}>
          <DataGrid
            rows={previewRows}
            columns={previewColumns}
            slots={{ toolbar }}
            autosizeOnMount
            autosizeOptions={autosizeOptions}
            density={density}
            onDensityChange={setDensity}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              '& .MuiDataGrid-toolbarContainer': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                mb: 1,
              },
            }}
          />
        </Box>
      </Stack>
    </Paper>
  );
};

export default SessionDatasetCard;
